import React, { useEffect, useMemo, useState } from 'react';
import { Contract, ContractLine, Customer } from '../types';
import Modal from './Modal';
import { apiService } from '../services/api';

interface BulkInvoiceCreationModalProps {
  contracts: Contract[];
  customers: Customer[];
  onClose: () => void;
  onComplete?: (createdCount: number, failedCount: number) => void | Promise<void>;
}

interface RowState {
  contract_id: string;
  contract_name: string;
  customer_id: number;
  currency: Contract['currency'];
  invoice_date: string; // yyyy-mm-dd
  due_date: string;     // yyyy-mm-dd
  billing_period_start: string; // yyyy-mm-dd
  billing_period_end: string;   // yyyy-mm-dd
  notes?: string;
  error?: string;
  status?: 'pending' | 'creating' | 'success' | 'failed';
}

const DATE_ONLY_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

function getMonthsFromBillingCycle(cycle?: Contract['billing_cycle']): number {
  switch (cycle) {
    case '3_months':
      return 3;
    case '12_months':
      return 12;
    case '1_month':
    default:
      return 1;
  }
}

function formatUTCDateOnly(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function extractDateOnly(value?: string | null): string {
  if (!value) {
    return '';
  }

  const match = value.trim().match(DATE_ONLY_PATTERN);
  if (match) {
    return match[1];
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : formatUTCDateOnly(parsed);
}

function parseDateOnlyToUTCDate(value: string): Date {
  const dateOnly = extractDateOnly(value);
  if (!dateOnly) {
    return new Date(NaN);
  }

  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function addMonthsUTC(date: Date, months: number): Date {
  const result = new Date(date);
  const originalDay = result.getUTCDate();

  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + months);

  const lastDayOfTargetMonth = new Date(
    Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)
  ).getUTCDate();

  result.setUTCDate(Math.min(originalDay, lastDayOfTargetMonth));
  return result;
}

function addDays(dateStr: string, days: number): string {
  const date = parseDateOnlyToUTCDate(dateStr);
  date.setUTCDate(date.getUTCDate() + days);
  return formatUTCDateOnly(date);
}

function getEarliestActiveLineStart(lines: ContractLine[]): string {
  let earliest = '';

  for (const line of lines) {
    if (!line.isactive || line.isdeleted) {
      continue;
    }

    const lineStart = extractDateOnly(line.start);
    if (!lineStart) {
      continue;
    }

    if (!earliest || lineStart < earliest) {
      earliest = lineStart;
    }
  }

  return earliest;
}

function getEarliestUnbilledLineStart(lines: ContractLine[], contractStart: string): string {
  let earliest = '';

  for (const line of lines) {
    if (!line.isactive || line.isdeleted) {
      continue;
    }

    const lineStart = extractDateOnly(line.start);
    if (!lineStart) {
      continue;
    }

    let unbilledStart = lineStart;
    if (line.last_billing_end) {
      unbilledStart = addDays(extractDateOnly(line.last_billing_end), 1);
      if (unbilledStart < lineStart) {
        unbilledStart = lineStart;
      }
    }

    const lineEnd = extractDateOnly(line.end);
    if (lineEnd && unbilledStart > lineEnd) {
      continue;
    }

    if (unbilledStart >= contractStart) {
      continue;
    }

    if (!earliest || unbilledStart < earliest) {
      earliest = unbilledStart;
    }
  }

  return earliest;
}

export function computePeriodForContract(c: Contract, lines: ContractLine[] = []): { start: string; end: string } {
  try {
    const contractStartValue =
      extractDateOnly(c.next_billing_start) ||
      getEarliestActiveLineStart(lines) ||
      extractDateOnly(c.start_date);

    if (!contractStartValue) {
      return { start: '', end: '' };
    }

    const contractStart = parseDateOnlyToUTCDate(contractStartValue);
    const invoiceStartValue =
      getEarliestUnbilledLineStart(lines, contractStartValue) ||
      contractStartValue;

    const invoiceStart = parseDateOnlyToUTCDate(invoiceStartValue);
    const end = addMonthsUTC(contractStart, getMonthsFromBillingCycle(c.billing_cycle));
    end.setUTCDate(end.getUTCDate() - 1);

    const contractEnd = parseDateOnlyToUTCDate(c.end_date || '');
    if (!Number.isNaN(contractEnd.getTime()) && end > contractEnd) {
      end.setTime(contractEnd.getTime());
    }

    if (end < invoiceStart) {
      end.setTime(invoiceStart.getTime());
    }

    return {
      start: formatUTCDateOnly(invoiceStart),
      end: formatUTCDateOnly(end)
    };
  } catch (error) {
    console.error(`Failed to compute billing period for contract ${c.contract_id}:`, error);
    return { start: '', end: '' };
  }
}

const formatDateUTC = (dateStr: string) => {
  const parsed = parseDateOnlyToUTCDate(dateStr);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString('de-DE', { timeZone: 'UTC' });
};

const BulkInvoiceCreationModal: React.FC<BulkInvoiceCreationModalProps> = ({ contracts, customers, onClose, onComplete }) => {
  const today = useMemo(() => formatUTCDateOnly(new Date()), []);

  const [rows, setRows] = useState<RowState[]>([]);
  const [contractLines, setContractLines] = useState<Record<string, ContractLine[]>>({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const fetchContractLines = async () => {
      if (contracts.length === 0) {
        setContractLines({});
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const entries = await Promise.all(
          contracts.map(async (contract) => {
            try {
              const lines = await apiService.getContractLines(contract.contract_id);
              return [contract.contract_id, lines] as const;
            } catch (error) {
              console.error(`Failed to fetch contract lines for contract ${contract.contract_id}:`, error);
              return [contract.contract_id, []] as const;
            }
          })
        );

        if (!isCancelled) {
          setContractLines(Object.fromEntries(entries));
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchContractLines();

    return () => {
      isCancelled = true;
    };
  }, [contracts]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (contracts.length === 0) {
      setRows([]);
      return;
    }

    const init = contracts.map((c) => {
      const period = computePeriodForContract(c, contractLines[c.contract_id] || []);
      const invoice_date = today;
      let due_date = c.currency === 'BRL' ? period.start : addDays(invoice_date, 14);
      if (due_date < today) due_date = today;

      return {
        contract_id: c.contract_id,
        contract_name: c.contract_name,
        customer_id: c.customer_id,
        currency: c.currency,
        invoice_date,
        due_date,
        billing_period_start: period.start,
        billing_period_end: period.end,
        notes: '',
        status: 'pending' as const
      };
    });

    setRows(init);
  }, [contracts, contractLines, loading, today]);

  const getCustomerName = (id: number) => customers.find((x) => x.id === id)?.customer_name || `Customer ${id}`;

  const setField = (idx: number, field: keyof RowState, value: any) => {
    setRows((prev) => {
      const copy = [...prev];
      // Adjust due date rule when changing invoice_date or start
      if (field === 'invoice_date') {
        const dd = copy[idx].due_date;
        const fallback = copy[idx].currency === 'BRL' ? copy[idx].billing_period_start : addDays(String(value), 14);
        let nextDue = dd || fallback;
        if (nextDue < today) nextDue = today;
        copy[idx] = { ...copy[idx], invoice_date: String(value), due_date: nextDue };
      } else if (field === 'billing_period_start') {
        const c = contracts.find((cc) => cc.contract_id === copy[idx].contract_id);
        let end = copy[idx].billing_period_end;
        if (c) {
          const months = getMonthsFromBillingCycle(c.billing_cycle);
          const start = parseDateOnlyToUTCDate(String(value));
          const computedEnd = addMonthsUTC(start, months);
          computedEnd.setUTCDate(computedEnd.getUTCDate() - 1);

          const contractEnd = parseDateOnlyToUTCDate(c.end_date || '');
          if (!Number.isNaN(contractEnd.getTime()) && computedEnd > contractEnd) {
            computedEnd.setTime(contractEnd.getTime());
          }

          if (computedEnd < start) {
            computedEnd.setTime(start.getTime());
          }

          end = formatUTCDateOnly(computedEnd);
        }
        let nextDue = copy[idx].due_date;
        if (copy[idx].currency === 'BRL') {
          nextDue = String(value);
        }
        if (nextDue < today) nextDue = today;
        copy[idx] = { ...copy[idx], billing_period_start: String(value), billing_period_end: end, due_date: nextDue };
      } else if (field === 'due_date') {
        let nextDue = String(value);
        if (nextDue < today) nextDue = today;
        copy[idx] = { ...copy[idx], due_date: nextDue };
      } else {
        copy[idx] = { ...copy[idx], [field]: value } as RowState;
      }
      return copy;
    });
  };

  const createAll = async () => {
    setCreating(true);
    let created = 0;
    let failed = 0;
    try {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        if (r.status === 'success') {
          continue;
        }

        setRows((prev) => {
          const copy = [...prev];
          copy[i] = { ...copy[i], status: 'creating', error: undefined };
          return copy;
        });

        if (!r.contract_id || !r.customer_id || !r.invoice_date || !r.due_date || !r.billing_period_start || !r.billing_period_end) {
          failed++;
          setRows((prev) => {
            const copy = [...prev];
            copy[i] = { ...copy[i], status: 'failed', error: 'Missing required fields' };
            return copy;
          });
          continue;
        }
        // Prepare payload without timezone conversion
        const asISO = (d: string) => `${d}T00:00:00`;
        try {
          await apiService.createInvoice({
            contract_id: r.contract_id,
            customer_id: r.customer_id,
            invoice_date: asISO(r.invoice_date),
            due_date: asISO(r.due_date),
            billing_period_start: asISO(r.billing_period_start),
            billing_period_end: asISO(r.billing_period_end),
            notes: r.notes || undefined,
            is_draft: true
          });
          created++;
          setRows((prev) => {
            const copy = [...prev];
            copy[i] = { ...copy[i], status: 'success' };
            return copy;
          });
        } catch (e: any) {
          failed++;
          const msg = e?.response?.data?.detail || e?.message || 'Creation failed';
          setRows((prev) => {
            const copy = [...prev];
            copy[i] = { ...copy[i], status: 'failed', error: msg };
            return copy;
          });
        }
      }
    } finally {
      setCreating(false);
      if (onComplete) {
        await onComplete(created, failed);
      }
    }
  };

  const successCount = rows.filter((r) => r.status === 'success').length;
  const failedCount = rows.filter((r) => r.status === 'failed').length;
  const remainingCount = rows.filter((r) => r.status !== 'success').length;

  if (contracts.length === 0) {
    return (
      <Modal title="Bulk Create Invoices" onClose={onClose}>
        <div className="alert alert-warning">
          No contracts selected for bulk invoice creation.
        </div>
      </Modal>
    );
  }

  if (loading) {
    return (
      <Modal title={`Bulk Create Invoices (${contracts.length})`} onClose={onClose}>
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading contract details...</p>
        </div>
      </Modal>
    );
  }

  if (rows.length === 0) {
    return (
      <Modal title={`Bulk Create Invoices (${contracts.length})`} onClose={onClose}>
        <div className="alert alert-warning">
          No invoice data available for the selected contracts.
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Bulk Create Invoices (${contracts.length})`} onClose={onClose}>
      <div className="table-responsive" style={{ maxHeight: '60vh', overflow: 'auto' }}>
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Contract</th>
              <th>Customer</th>
              <th>Currency</th>
              <th>Invoice Date</th>
              <th>Due Date</th>
              <th>Period Start</th>
              <th>Period End</th>
              <th>Notes</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.contract_id}>
                <td style={{ whiteSpace: 'nowrap' }}>{row.contract_name}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{getCustomerName(row.customer_id)}</td>
                <td><span className="badge badge-info">{row.currency}</span></td>
                <td>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={row.invoice_date}
                    onChange={(e) => setField(idx, 'invoice_date', e.target.value)}
                    disabled={row.status === 'creating' || row.status === 'success'}
                  />
                  <div className="form-text text-muted">{formatDateUTC(row.invoice_date)}</div>
                </td>
                <td>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={row.due_date}
                    onChange={(e) => setField(idx, 'due_date', e.target.value)}
                    disabled={row.status === 'creating' || row.status === 'success'}
                  />
                  <div className="form-text text-muted">{formatDateUTC(row.due_date)}</div>
                </td>
                <td>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={row.billing_period_start}
                    onChange={(e) => setField(idx, 'billing_period_start', e.target.value)}
                    disabled={row.status === 'creating' || row.status === 'success'}
                  />
                  <div className="form-text text-muted">{formatDateUTC(row.billing_period_start)}</div>
                </td>
                <td>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={row.billing_period_end}
                    onChange={(e) => setField(idx, 'billing_period_end', e.target.value)}
                    disabled={row.status === 'creating' || row.status === 'success'}
                  />
                  <div className="form-text text-muted">{formatDateUTC(row.billing_period_end)}</div>
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row.notes || ''}
                    placeholder="Notes (optional)"
                    onChange={(e) => setField(idx, 'notes', e.target.value)}
                    disabled={row.status === 'creating' || row.status === 'success'}
                  />
                </td>
                <td style={{ minWidth: 120 }}>
                  {row.status === 'pending' && <span className="badge badge-secondary">Pending</span>}
                  {row.status === 'creating' && <span className="badge badge-info">Creating…</span>}
                  {row.status === 'success' && <span className="badge badge-success">Created</span>}
                  {row.status === 'failed' && (
                    <span className="badge badge-danger" title={row.error}>Failed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <div>
          <span className="badge badge-success" style={{ marginRight: 8 }}>Created: {successCount}</span>
          <span className="badge badge-danger">Failed: {failedCount}</span>
        </div>
        <div>
          <button className="btn btn-secondary" onClick={onClose} disabled={creating}>Close</button>
          <button
            className="btn btn-primary"
            onClick={createAll}
            style={{ marginLeft: 8 }}
            disabled={creating || remainingCount === 0}
            title="Create remaining invoices"
          >
            {creating ? 'Creating…' : remainingCount === rows.length ? 'Create Invoices' : 'Create Remaining'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkInvoiceCreationModal;
